import { SiTrackGrid } from '../../../../components/si-track-grid/si-track-grid';
import { columns } from './services-grid-columns';
import { servicesApiKeyStore } from '../../../../api/masters/services-api-key-store';
import { UserPermissionName } from '../../../../models/user-permission-name';
import React, { useEffect, useState } from 'react';
import { gridApiKeyStore } from '../../../../api/grid-api-key-store';
import { useLoadData } from '../../../../hooks/useLoadData';
import { ApiResponse } from '../../../../models/api-response';
import { GridColumnConfig, GridPreference } from '../../../../models/gridCoumnConfig';
import { Service } from '../../../../models/masters/service';
import { useSaveData } from '../../../../hooks/useSaveData';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../../../hooks/use-debounce';
import { Grid, TextField, Typography } from '@mui/material';
import { LoadingIndicator } from '../../../../components/loading-indicator.component';
import { projectsApiKeyStore } from '../../../../api/projects-api-key-store';
import { ReactHookForm } from '../../../../components/hook-form/react-hook-form.component';
import { ServicePricingFormComponent } from './service-pricing-from.component';
import { useReactHookForm } from '../../../../hooks/useReactHookForm';

interface ProjectsTabProps {
  projectID: number;
  pricingListID: any;
}

interface CellProps {
  projectService: any;
}

interface CustomGridCellProps {
  field: string;
}

const DEFAULT_GROUP = [
  {
    field: 'serviceGroupName',
  },
];

export const ServicesTab = ({ projectID, pricingListID }: ProjectsTabProps) => {

  const form = useReactHookForm({
    defaultValues: {
      projectPrice: { pricingListID: '', pricingListName: "" },
      priceStartDate: "",
      priceEndDate: "",
      priceApplyTo: "",
    },
  });

  const [serviceDetails, setServiceDetails] = useState<any>();
  const [selectedPricingID, setSelectedPricingID] = useState<any>(0);

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('Project Details', 'Project Service'));

  const {
    isLoading,
    data: Services,
    refetch,
  } = useLoadData<ApiResponse<Service[]>>(servicesApiKeyStore.getServiceProjectMappings(projectID));

  const { data: pricingListData,
    isLoading: isPricingLoading, isFetching: isPricingFetching } = useLoadData<ApiResponse<any>>(
      projectsApiKeyStore.getProjectPricingDetailsByProjectID(projectID, selectedPricingID)
    );

  useEffect(() => {
    if (Services) setServiceDetails(Services?.data);
  }, [Services])

  useEffect(() => {
    if (pricingListData) {
      setServiceDetails(pricingListData?.data?.services?.filter((d: Service) => !d.isNotReadyService));
    }
  }, [pricingListData])

  const IsActiveCell = React.memo(({ projectService }: CellProps) => {
    return (
      <td className="stickyCloumn">
        <Typography variant='body1'>{projectService?.dataItem?.isActive ? 'Active' : 'Inactive'}</Typography>
      </td>
    );
  });

  const customGridCell = ({ field }: CustomGridCellProps): ((dataItem: any) => JSX.Element) | undefined => {
    if (field === 'isActive') {
      return (dataItem: any) => {
        return <IsActiveCell projectService={dataItem} />;
      };
    }
    // Return undefined if the field is not 'bluePrint'
    return undefined;
  };

  const handlePriceChange = (pricingID: any) => {
    if (!pricingID) setServiceDetails(Services?.data)
    setSelectedPricingID(pricingID)
  }
  // -------------------------------------------------Grid Layout------------------------------

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredServices, setFilteredServices] = useState<Service[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    if (serviceDetails) {
      const filteredServicetList = serviceDetails?.filter((Project: any) =>
        Object.values(Project).some(
          (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
        )
      );
      setFilteredServices(filteredServicetList || []);
    }
  }, [debouncedSearchTerm, serviceDetails]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'Project Service',
      pageName: 'Project Details',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'Project Service',
      pageName: 'Project Details',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  return (
    <ReactHookForm formName="PROJECT_PRICING_SERVICE_TAB_FORM" onSubmit={() => { }} formContextProps={form} isBusy={isLoading}>
      <Grid container spacing={2}>
        <ServicePricingFormComponent projectID={projectID} pricingID={pricingListID} onSelectPrice={handlePriceChange} />
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <SiTrackGrid
            fileTitle='project_service'
            columns={columns}
            dataItemKey="serviceID"
            // queryConfig={servicesApiKeyStore.getServiceProjectMappings(projectID)}
            // defaultGroup={DEFAULT_GROUP}
            customCell={(field) =>
              customGridCell?.({
                field,
              })
            }
            actionPermissions={{
              export: UserPermissionName.DOWNLOAD_GRID,
            }}
            dataItems={filteredServices || []}
            reorderable
            handleSearch={handleInputChange}
            handleSaveLayout={handleSaveLayout}
            isSaveLayout={true}
            gridConfigFromApi={gridConfig}
            handleResetLayout={handleResetLayout}
            isBusy={isLoading || isLoadingGridLayout || isPricingLoading || isPricingFetching}
          // -----------------Grid Layout-------
          />
        </Grid>
      </Grid>
    </ReactHookForm>
  );
};
